import { useEffect } from 'react'

/**
 * useIdlePrefetch — fire a list of dynamic import() functions during
 * browser idle time so the chunks are cached in memory before the user
 * navigates to them.  Restores the offline navigation guarantee that
 * pure route-based lazy-loading would otherwise remove (Opus 5 pre-code
 * finding: "users of a desktop time-tracker offline is the normal case").
 *
 * Usage:
 *   useIdlePrefetch(() => [
 *     () => import('../pages/Reports'),
 *     () => import('../pages/Trust'),
 *   ])
 *
 * Architecture (refined 2026-08-10 per post-code review — the earlier
 * parallel-dispatch version had a real bug: `requestIdleCallback`'s
 * `timeout` option is a DEADLINE, not a start-time stagger, so all
 * imports fired in the same idle window, competing with real page
 * navigation for bandwidth and parse time — flagged by Sol, Kimi,
 * and Grok):
 *
 * - Fires imports SEQUENTIALLY, chaining via `.finally()`.  Each
 *   completes (or fails) before the next is scheduled.  Caps total
 *   parallelism at 1 — plenty for a background prefetch that runs
 *   during idle, and no risk of hammering the network on cold cache.
 * - Uses requestIdleCallback where available; setTimeout fallback
 *   for Safari and older Electron.
 * - Wraps each `fn()` in `Promise.resolve().then(fn)` so sync throws
 *   from importer functions don't escape the try/catch (Kimi finding).
 * - Silently swallows errors — this is a HINT, not a correctness path.
 *   Real chunk-load failures still hit ChunkLoadErrorBoundary if the
 *   user hits a route before the prefetch completes.
 * - Cancels pending idle callbacks + skips remaining imports on
 *   unmount so we don't leak background work when the window closes
 *   mid-prefetch.
 */
export default function useIdlePrefetch (getImports) {
  useEffect(() => {
    const imports = typeof getImports === 'function' ? getImports() : getImports
    if (!Array.isArray(imports) || imports.length === 0) return

    const ric = window.requestIdleCallback ||
      ((cb) => setTimeout(() => cb({ timeRemaining: () => 50, didTimeout: false }), 2000))
    const cic = window.cancelIdleCallback || clearTimeout

    let cancelled = false
    let currentHandle = null
    let idx = 0

    // Chain — schedule each import after the previous settles.  Serial
    // dispatch avoids the 15-21 chunk burst that all-parallel dispatch
    // produces (the naive rIC-with-timeout approach doesn't stagger
    // because rIC `timeout` is a deadline, not a delay).
    function scheduleNext () {
      if (cancelled || idx >= imports.length) return
      const fn = imports[idx++]
      currentHandle = ric(
        () => {
          if (cancelled) return
          // Promise.resolve().then wraps in a promise so sync throws
          // from `fn()` land in the .catch rather than escaping.
          Promise.resolve()
            .then(() => fn())
            .catch(() => {}) // Swallow — best-effort prefetch
            .finally(() => {
              if (!cancelled) scheduleNext()
            })
        },
        { timeout: 5000 }
      )
    }

    scheduleNext()

    return () => {
      cancelled = true
      if (currentHandle !== null) {
        try { cic(currentHandle) } catch (e) {}
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}
